import React from 'react';
import { connect } from 'react-redux';
import { Button, Popconfirm, Divider, message } from 'antd';
import Card from '../../../../../components/Card/Card';
import Modal from '../../../../../components/Modal/Modal';
import ModuleEmptyState from '../../../../../components/ModuleEmptyState';
import Table from '../../../../../components/Table/Table';
import Export from '../../../../../components/Export/Export';
import Import from '../../../../../components/Import/Import';
import StrategicThirdPartyEdit from './StrategicThirdPartyEdit';
import * as actions from '../StrategicThirdPartyAction';
import { orgIdSelector, businessNameSelector } from '../../../OrganisationSelectors';
import { loadingStrategicThirdPartySelector } from '../StrategicThirdPartySelectors';
import { MSG_ERROR } from '../../../../../constants';
import { getApiErrorMessage } from '../../../../../utils/httpError';

const StrategicThirdPartyClass = ({
  orgId,
  businessName,
  strategicThirdPartyClass,
  strategicThirdPartyList,
  strategicThirdPartyCategories,
  deleteStrategicThirdParty,
  downloadStrategicThirdPartyImportTemplate,
  exportStrategicThirdParty,
  importStrategicThirdParty,
  surfaceOnly = false
}) => {
  const onDelete = async strategicThirdParty => {
    try {
      await deleteStrategicThirdParty(orgId, strategicThirdParty.strategicThirdPartyId);
      message.success('The strategic third party has been successfully deleted');
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const strategicThirdPartyCategoryFilters = (strategicThirdPartyCategories || []).map(obj => ({
    text: obj.name,
    value: obj.name
  }));

  const columns = [
    { key: 'name', title: 'Business Name', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    {
      key: 'category',
      title: 'Category',
      filters: strategicThirdPartyCategoryFilters,
      onFilter: (value, record) => record.strategicThirdPartyCategory.name.indexOf(value) === 0,
      render: (text, record) => (
        <span>
          {record.strategicThirdPartyCategory.name === 'Other'
            ? record.strategicThirdPartyCategoryText
            : record.strategicThirdPartyCategory.name}
        </span>
      )
    },
    {
      key: 'contactPerson',
      title: 'Contact Person',
      dataIndex: 'contactPerson'
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      render: text => (text ? <a href={`mailto:${text}`}>{text}</a> : null)
    },
    {
      key: 'phone',
      title: 'Phone',
      dataIndex: 'phone',
      render: text => (text ? <a href={`tel:${text}`}>{text}</a> : null)
    },
    {
      key: 'action',
      width: 115,
      render: (text, record) => (
        <span>
          <Modal explanation toggle={show => <Button type="link" icon="edit" onClick={show} />}>
            {(hide, setExplanation) => (
              <StrategicThirdPartyEdit
                strategicThirdPartyInfo={record}
                strategicThirdPartyCategories={strategicThirdPartyCategories}
                close={hide}
                onExplanationChange={setExplanation}
              />
            )}
          </Modal>
          <Divider type="vertical" />
          <Popconfirm title="Sure to delete?" onConfirm={() => onDelete(record)}>
            <Button type="link" icon="delete" />
          </Popconfirm>
        </span>
      )
    }
  ];

  const strategicThirdPartyClassId =
    strategicThirdPartyCategories && strategicThirdPartyCategories.length > 0
      ? strategicThirdPartyCategories[0].strategicThirdPartyClassId
      : 0;
  const hasRows = Array.isArray(strategicThirdPartyList) && strategicThirdPartyList.length > 0;
  const addModal = button => (
    <Modal explanation toggle={show => button(show)}>
      {(hide, setExplanation) => (
        <StrategicThirdPartyEdit
          strategicThirdPartyCategories={strategicThirdPartyCategories}
          close={hide}
          onExplanationChange={setExplanation}
        />
      )}
    </Modal>
  );
  const importModal = button => (
    <Modal title={`Import ${strategicThirdPartyClass}`} toggle={show => button(show)}>
      {() => (
        <Import
          templateName={`${strategicThirdPartyClass.replace(/\s+/g, '_')}_template.xlsx`}
          downloadTemplate={() => downloadStrategicThirdPartyImportTemplate(strategicThirdPartyClassId)}
          importFromTemplate={file => importStrategicThirdParty(orgId, file, strategicThirdPartyClassId)}
        />
      )}
    </Modal>
  );

  return (
    <Card title={surfaceOnly ? null : strategicThirdPartyClass}>
      <Table
        title={strategicThirdPartyClass}
        columns={columns}
        dataSource={strategicThirdPartyList}
        rowKey="strategicThirdPartyId"
        searchFields={['name', 'category', 'contactPerson', 'email', 'phone']}
        moduleEmpty={
          <ModuleEmptyState
            moduleTitle={strategicThirdPartyClass}
            embedded
            headline={`No ${strategicThirdPartyClass.toLowerCase()} yet`}
            description={`Add or import the ${strategicThirdPartyClass.toLowerCase()} that may be needed during a disruption.`}
            iconType={strategicThirdPartyClass === 'Strategic Crisis Experts' ? 'solution' : 'team'}
          >
            {addModal(show => (
              <Button type="primary" className="module-empty-state__btn-primary" htmlType="button" onClick={show}>
                Add First
              </Button>
            ))}
            {importModal(show => (
              <Button className="module-empty-state__btn-secondary" htmlType="button" onClick={show}>
                Import from CSV
              </Button>
            ))}
          </ModuleEmptyState>
        }
      />
      {!surfaceOnly && hasRows ? (
        <div className="btn-container">
          <Export
            fileName={`${businessName.replace(/\s+/g, '_')}_${strategicThirdPartyClass.replace(/\s+/g, '_')}.xlsx`}
            exportFile={() => exportStrategicThirdParty(orgId, strategicThirdPartyClassId)}
          >
            {onExport => (
              <Button type="default" icon="export" onClick={onExport}>
                Export
              </Button>
            )}
          </Export>
          {importModal(show => (
              <Button type="default" icon="contacts" onClick={show}>
                Import
              </Button>
          ))}
          {addModal(show => (
              <Button type="primary" onClick={show}>
                Add new
              </Button>
          ))}
        </div>
      ) : null}
    </Card>
  );
};

const mapStateToProps = state => ({
  orgId: orgIdSelector(state),
  businessName: businessNameSelector(state),
  loading: loadingStrategicThirdPartySelector(state)
});

const mapDispatchToProps = { ...actions };

export default connect(mapStateToProps, mapDispatchToProps)(StrategicThirdPartyClass);
